import type { CellarDTO, GridCell, ShelfDTO } from '@adega/shared';
import { URGENCY_LABELS, nomeCompleto } from '@adega/shared';

interface Props {
  cellar: CellarDTO;
  /** Quando definido, a célula vira alvo de seleção em vez de link para detalhe. */
  onSelecionarVaga?: (shelf: ShelfDTO, position: number) => void;
  onAbrirGarrafa?: (bottleId: number) => void;
  /** Vagas destacadas. Aceita várias: adicionar garrafas iguais marca várias de uma vez. */
  vagasSelecionadas?: ReadonlyArray<{ shelfId: number; position: number }>;
}

const COR_URGENCIA: Record<string, string> = {
  beber_agora: 'ring-2 ring-red-400/80',
  beber_este_ano: 'ring-2 ring-amber-300/70',
};

export function CellarGrid({
  cellar,
  onSelecionarVaga,
  onAbrirGarrafa,
  vagasSelecionadas = [],
}: Props) {
  return (
    <div className="space-y-4">
      {cellar.shelves.map((shelf) => (
        // p-3 no celular: cada pixel de largura vira espaço para as garrafas.
        <section key={shelf.id} className="card p-3 sm:p-4">
          <header className="mb-3 flex items-baseline justify-between gap-2">
            <h3 className="font-semibold">{shelf.label}</h3>
            <span className="text-xs text-adega-muted">
              {shelf.occupied}/{shelf.capacity} ocupadas
            </span>
          </header>

          {/* Colunas se ajustam à largura. Mínimo de 3rem para que uma prateleira
              de 5 garrafas caiba numa fileira só — como é fisicamente — também
              nos celulares de 360px, os mais comuns. */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(3rem,1fr))] gap-1.5">
            {shelf.cells.map((cell) => (
              <Celula
                key={cell.position}
                cell={cell}
                selecionada={vagasSelecionadas.some(
                  (vaga) => vaga.shelfId === shelf.id && vaga.position === cell.position,
                )}
                onClick={() => {
                  if (cell.bottle && onAbrirGarrafa) {
                    onAbrirGarrafa(cell.bottle.id);
                  } else if (!cell.bottle && onSelecionarVaga) {
                    onSelecionarVaga(shelf, cell.position);
                  }
                }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Celula({
  cell,
  selecionada,
  onClick,
}: {
  cell: GridCell;
  selecionada: boolean;
  onClick: () => void;
}) {
  const { bottle } = cell;
  const numero = cell.position + 1;

  if (!bottle) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`Posição ${numero} livre${selecionada ? ' (selecionada)' : ''}`}
        aria-pressed={selecionada}
        className={`flex aspect-3/4 flex-col items-center justify-center rounded-lg border border-dashed text-adega-muted transition ${
          selecionada
            ? 'border-adega-gold bg-adega-gold/15 text-adega-gold'
            : 'border-adega-border hover:border-adega-wine-light hover:text-adega-text'
        }`}
      >
        <span className="text-lg leading-none">＋</span>
        <span className="mt-1 text-xs">{numero}</span>
      </button>
    );
  }

  const destaque = COR_URGENCIA[bottle.urgency] ?? '';
  const descricao = `${nomeCompleto(bottle)}${bottle.vintage ? ` ${bottle.vintage}` : ''}, posição ${numero} — ${URGENCY_LABELS[bottle.urgency]}`;

  return (
    <button
      type="button"
      onClick={onClick}
      title={descricao}
      aria-label={descricao}
      className={`relative flex aspect-3/4 flex-col justify-between overflow-hidden rounded-lg border border-adega-border bg-adega-surface-2 text-left transition active:scale-95 ${destaque}`}
    >
      {bottle.thumbUrl ? (
        <img
          src={bottle.thumbUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}

      {/* Número da posição: é o que a pessoa procura ao olhar a adega física. */}
      <span className="relative m-1 self-start rounded-md bg-black/70 px-1.5 text-xs font-semibold leading-5">
        {numero}
      </span>

      <span className="relative bg-linear-to-t from-black/90 via-black/60 to-transparent px-1 pb-1 pt-3 text-center">
        {/* Com foto, o rótulo já identifica a garrafa — nome em letra miúda numa
            célula de ~60px só atrapalharia. Sem foto, o nome é o que resta. */}
        {!bottle.thumbUrl ? (
          <span className="line-clamp-3 text-[11px] font-medium leading-tight">{nomeCompleto(bottle)}</span>
        ) : null}
        {bottle.vintage ? (
          <span className="block text-xs font-semibold">{bottle.vintage}</span>
        ) : null}
      </span>
    </button>
  );
}
